import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { TICKET_PRIORITIES, PRIORITY_LABELS, userLabel } from '../lib/tickets.js';
import { InitialsAvatar } from './TicketBits.jsx';
import SiteSearchSelect from './SiteSearchSelect.jsx';

// Chip-style multi-select for collaborators. Exported so the ticket
// detail sidebar can reuse it. `excludeUserId` keeps the primary owner
// out of the addable list (no double-stacking roles).
export function CollaboratorPicker({ users, selectedIds, excludeUserId, currentUserId, onAdd, onRemove }) {
  const [adding, setAdding] = useState(false);
  const selected = users.filter((u) => selectedIds.includes(u.id));
  const addable = users.filter(
    (u) => !selectedIds.includes(u.id) && u.id !== excludeUserId
  );
  return (
    <div className="collab-picker">
      {selected.length === 0 && <span className="cell-mono cell-muted">No collaborators</span>}
      {selected.map((u) => (
        <span key={u.id} className="collab-chip">
          <InitialsAvatar user={u} size={18} />
          <span>{userLabel(u, currentUserId)}</span>
          <button type="button" className="collab-chip-x" onClick={() => onRemove(u.id)} aria-label={`Remove ${u.name || u.email}`}>×</button>
        </span>
      ))}
      {adding ? (
        <select
          className="collab-add-select"
          autoFocus
          defaultValue=""
          onChange={(e) => { const v = Number(e.target.value); if (v) onAdd(v); setAdding(false); }}
          onBlur={() => setAdding(false)}
        >
          <option value="" disabled>Add collaborator…</option>
          {addable.map((u) => <option key={u.id} value={u.id}>{userLabel(u, currentUserId)}</option>)}
        </select>
      ) : (
        addable.length > 0 && (
          <button type="button" className="collab-add-btn" onClick={() => setAdding(true)}>+ Add</button>
        )
      )}
    </div>
  );
}

export default function NewTicketModal({
  open, users: usersProp, sites: sitesProp, currentUserId, preselectedSiteId, onCreated, onCancel
}) {
  const [users, setUsers] = useState(usersProp || []);
  const [sites, setSites] = useState(sitesProp || []);
  const [siteId, setSiteId] = useState(preselectedSiteId ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [primaryAssigneeId, setPrimaryAssigneeId] = useState(currentUserId ?? '');
  const [collaboratorIds, setCollaboratorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fetchedOnce = useRef(false);

  useEffect(() => {
    if (!open) return;
    setSiteId(preselectedSiteId ?? '');
    setTitle(''); setDescription(''); setPriority('normal');
    setPrimaryAssigneeId(currentUserId ?? '');
    setCollaboratorIds([]);
    setError(null); setSaving(false);
    // Lazy-load pickers if the caller didn't pass them.
    if ((!usersProp || !sitesProp) && !fetchedOnce.current) {
      fetchedOnce.current = true;
      axios.get('/api/tickets/meta')
        .then((r) => { setUsers(r.data.users || []); setSites(r.data.sites || []); })
        .catch(() => {});
    } else {
      if (usersProp) setUsers(usersProp);
      if (sitesProp) setSites(sitesProp);
    }
  }, [open, preselectedSiteId, currentUserId, usersProp, sitesProp]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel, saving]);

  if (!open) return null;

  // Primary must not also be a collaborator.
  const effCollabs = collaboratorIds.filter((id) => id !== Number(primaryAssigneeId));
  const selectedSite = sites.find((s) => s.id === Number(siteId));

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!title.trim()) return setError('Title is required.');
    if (!description.trim()) return setError('Description is required.');
    setSaving(true);
    try {
      const payload = {
        siteId: siteId ? Number(siteId) : null,
        siteName: selectedSite ? selectedSite.displayName : null,
        title: title.trim(),
        description: description.trim(),
        priority,
        primaryAssigneeId: primaryAssigneeId ? Number(primaryAssigneeId) : null,
        collaboratorIds: effCollabs
      };
      const res = await axios.post('/api/tickets', payload);
      if (onCreated) onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="New ticket" style={{ maxWidth: 640 }}>
        <header className="modal-head">
          <h3>New ticket</h3>
          <button type="button" className="modal-close" onClick={onCancel} disabled={saving} aria-label="Close">×</button>
        </header>
        <form className="modal-body" onSubmit={submit}>
          <p className="modal-sub" style={{ marginTop: 0 }}>
            Track a support issue. You can add notes after creating it.
          </p>

          {/* Site picker (skip when preselected) — searchable across
              site #, name, city, and customer name. */}
          {preselectedSiteId == null && (
            <label className="form-field form-field-wide">
              <span>Site <span className="form-hint">(leave blank for an internal ticket)</span></span>
              <SiteSearchSelect
                sites={sites}
                value={siteId}
                onChange={(id) => setSiteId(id)}
                allowInternal
                placeholder="Search by site #, name, city, or customer…"
              />
              {selectedSite && <span className="form-hint">Selected: {selectedSite.displayName}{selectedSite.city ? ` · ${selectedSite.city}` : ''}</span>}
            </label>
          )}

          {/* Preselected site (opened from a site/renewals row) — shown
              read-only with name + number so the ticket is clearly tied
              to it. */}
          {preselectedSiteId != null && (
            <label className="form-field form-field-wide">
              <span>Site</span>
              <div className="ticket-preselected-site cell-mono">
                {selectedSite
                  ? [selectedSite.displayName, selectedSite.siteNumber].filter(Boolean).join('  ·  ')
                  : 'Loading site…'}
              </div>
            </label>
          )}

          <label className="form-field form-field-wide">
            <span>Title <span className="req">*</span></span>
            <input type="text" maxLength={200} autoFocus value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary, e.g. SFTP failing intermittently" />
          </label>

          <label className="form-field form-field-wide">
            <span>Description <span className="req">*</span></span>
            <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What's happening, what you've tried, any error messages…" />
          </label>

          <div className="form-grid">
            <label className="form-field">
              <span>Priority</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                {TICKET_PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Primary owner</span>
              <select value={primaryAssigneeId} onChange={(e) => setPrimaryAssigneeId(e.target.value)}>
                <option value="">Unassigned</option>
                {users.map((u) => <option key={u.id} value={u.id}>{userLabel(u, currentUserId)}</option>)}
              </select>
            </label>
          </div>

          <label className="form-field form-field-wide">
            <span>Collaborators <span className="form-hint">(optional)</span></span>
            <CollaboratorPicker
              users={users}
              selectedIds={effCollabs}
              excludeUserId={Number(primaryAssigneeId) || null}
              currentUserId={currentUserId}
              onAdd={(id) => setCollaboratorIds((p) => [...new Set([...p, id])])}
              onRemove={(id) => setCollaboratorIds((p) => p.filter((x) => x !== id))}
            />
          </label>

          {error && <p className="error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || !title.trim() || !description.trim()}>
              {saving ? 'Creating…' : 'Create ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
